import { useRef } from 'react';
import { shallowEqual } from 'react-redux';
import { Accordion } from '@zillow/constellation-10';

import type { DatarouterError } from '@zg-rentals/rental-manager-env';

import { useSelector } from '../../../selectors/useSelector';
import TenantAccordionItem from './TenantAccordionItem';
import TenantDirectoryCSEvents from '../../../model/ClickstreamEvent/TenantDirectoryCSEvents';

import type {
  RentalManagerRenterContactTenantWithTenanciesDto,
  RentalManagerRenterUpdateRenterResponseDto,
  RenterInfoRenterAddedDto,
} from '@zg-rentals/ts-rental-manager-api-client';

// Types
type TenantAccordionListProps = {
  tenants: Array<RentalManagerRenterContactTenantWithTenanciesDto>;
  onDeleteTenancyPeriodSuccess?: () => void | null;
  onDeleteTenancyPeriodError?: (error: DatarouterError) => void | null;
  onDeleteTenantSuccess?: () => void | null;
  onDeleteTenantError?: (error: DatarouterError) => void | null;
  onEditTenantSuccess?: (updatedRenterDto?: RentalManagerRenterUpdateRenterResponseDto) => void | null;
  onLeaseTermActionSuccess?: ({
    addedRenters,
    isEditMode,
  }: {
    addedRenters?: Array<RenterInfoRenterAddedDto>;
    isEditMode: boolean;
  }) => void | null;
  onLeaseTermActionError?: (error: DatarouterError) => void | null;
  onEditTenantError?: (error: DatarouterError) => void | null;
  onRequestProofSuccess?: (message: string) => void | null;
  onRequestProofError?: (message: string) => void | null;
};

const TenantAccordionList = ({
  tenants,
  onDeleteTenancyPeriodSuccess,
  onDeleteTenancyPeriodError,
  onDeleteTenantSuccess,
  onDeleteTenantError,
  onEditTenantSuccess,
  onLeaseTermActionSuccess,
  onEditTenantError,
  onLeaseTermActionError,
  onRequestProofSuccess,
  onRequestProofError,
}: TenantAccordionListProps) => {
  const previousExpandedRef = useRef<Array<string>>(['tenant-0']); // Track previous expanded state

  const { currentProperty, user } = useSelector(
    (state) => ({
      currentProperty: state.properties.currentProperty,
      user: state.user,
    }),
    shallowEqual,
  );

  const handleExpandedChange = (value: string | Array<string>) => {
    // Convert value to array format for easier comparison
    const currentExpanded = Array.isArray(value) ? value : [value];
    const previousExpanded = previousExpandedRef.current;

    // Find newly expanded items (items in current but not in previous)
    const newlyExpanded = currentExpanded.filter((item) => !previousExpanded.includes(item));

    // Log when an accordion item is expanded
    if (newlyExpanded.length > 0) {
      newlyExpanded.forEach((itemValue) => {
        // Trigger clickstream event on expand
        TenantDirectoryCSEvents.TenantAccordionItemExpandClick({
          user,
          currentProperty,
        });
      });
    }

    // Update the ref with current expanded state for next comparison
    previousExpandedRef.current = currentExpanded;
  };

  return (
    <Accordion.Root
      defaultExpanded={['tenant-0']}
      data-testid="TenantListAccordion"
      data-eventid={TenantDirectoryCSEvents.TenantAccordionItemExpandClick.data_event_id}
      onExpandedChange={handleExpandedChange}
    >
      {tenants.map((tenant, index) => (
        <TenantAccordionItem
          key={index}
          tenant={tenant}
          index={index}
          onDeleteTenancyPeriodSuccess={onDeleteTenancyPeriodSuccess}
          onDeleteTenancyPeriodError={onDeleteTenancyPeriodError}
          onDeleteTenantSuccess={onDeleteTenantSuccess}
          onDeleteTenantError={onDeleteTenantError}
          onEditTenantSuccess={onEditTenantSuccess}
          onEditTenantError={onEditTenantError}
          onLeaseTermActionSuccess={onLeaseTermActionSuccess}
          onLeaseTermActionError={onLeaseTermActionError}
          onRequestProofSuccess={onRequestProofSuccess}
          onRequestProofError={onRequestProofError}
        />
      ))}
    </Accordion.Root>
  );
};

export default TenantAccordionList;
